import { type ReactElement, useSyncExternalStore } from 'react';
import {
  shouldHoldUpdatesForFormFill,
  subscribeFormFill,
} from '../lib/forms/formFillGuard';
import { AppVersionFooter } from './AppVersionFooter';
import { ForcedUpdateModal } from './ForcedUpdateModal';
import { UpdateAvailableBanner } from './UpdateAvailableBanner';
import { useAppVersionCheck } from './useAppVersionCheck';
import type { VersionInfo } from './types';

export interface AppVersionProps {
  /** The build the running client booted with (import from generated/appVersion.json). */
  current: VersionInfo;
  /** Pixels to lift the footer chip above a bottom nav bar (0 for the CRA app). */
  bottomOffset?: number;
  /** Where the published version.json lives (defaults to /version.json). */
  url?: string;
  /**
   * Optional route signal (e.g. useLocation().pathname) to re-check on navigation.
   * Omit to rely on interval + window focus.
   */
  routeKey?: string;
}

/**
 * Single global mount for the whole version system. Mount ONCE per app at the
 * global mount point. Renders the always-visible footer chip, plus the soft
 * update banner or the forced update modal as detection warrants. Fully
 * offline-safe — when version.json is unreachable it renders only the footer.
 */
export function AppVersion({
  current,
  bottomOffset = 0,
  url,
  routeKey,
}: AppVersionProps): ReactElement {
  const { latest, updateAvailable, forceUpdate, applyUpdate, lastCheckedAt } =
    useAppVersionCheck({ current, url, routeKey });
  // #4741: never put update UI over an open form. The version can already be
  // known when a fill mounts (lazy form chunk loads after the first check; a
  // support floor can turn soft→forced mid-fill), and the forced modal is
  // non-dismissible — over a form it is a lockout. Bounded by the guard's cap.
  const holdForFormFill = useSyncExternalStore(
    subscribeFormFill,
    shouldHoldUpdatesForFormFill,
  );
  const showUpdateUi = updateAvailable && !!latest && !holdForFormFill;

  return (
    <>
      <AppVersionFooter current={current} bottomOffset={bottomOffset} />
      {showUpdateUi && latest && forceUpdate && (
        <ForcedUpdateModal current={current} latest={latest} onUpdate={applyUpdate} />
      )}
      {showUpdateUi && latest && !forceUpdate && (
        <UpdateAvailableBanner
          current={current}
          latest={latest}
          onUpdate={applyUpdate}
          dismissKey={current.app}
          checkSignal={lastCheckedAt}
        />
      )}
    </>
  );
}
